/**
 * §1 — "the one fact this page exists to deliver".
 *
 * `client/widget-chatbot.html` and `client/widget-booking.html` open with the same
 * three columns: the count at hero size, the cost so far a rank below it, and the
 * three facts about the installation. The page's **one** `HeroFigure` is here
 * (CONTRACT.md §4.6); `WidgetUsagePanel` deliberately renders none.
 *
 * Two of the mockups' lines are not here, and both are recorded in the report:
 *
 *   - "Between 38 and 69 a day, averaging 53" would be a minimum and a maximum
 *     computed in the browser over `series.days`, and a page computes nothing
 *     (`.docs/19` §Design decisions). The day counter the same caption carries —
 *     "24 of 31 days" — is `days_elapsed` / `days_in_period` and is here.
 *   - "Bills in arrears on your 1 September invoice, as one line" names a date no
 *     field carries. What it is really saying is policy, and P7 puts policy in the
 *     one "how this works" fold rather than under a figure.
 */

import { isMetered, type WidgetSummaryView, type WidgetView } from '@fareast/shared'

import { Kv, KvRow } from '../../../components/portal/Kv.js'
import { Region, RegionCols } from '../../../components/portal/Region.js'
import { Stack } from '../../../components/portal/Layout.js'
import {
  Answer,
  B,
  DayText,
  HeroCap,
  HeroFigure,
  Muted,
  Small,
  SubFigure,
} from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import { widgetPriceWords } from '../../../format/widgetPrice.js'
import { BILLING_MODE_SENTENCE, nounOf, pluralOf, sentenceCase } from './text.js'

export type WidgetHeadlineProps = {
  readonly widget: WidgetView
  readonly summary: WidgetSummaryView
}

export function WidgetHeadline({ widget, summary }: WidgetHeadlineProps) {
  const format = useFormat()
  const noun = nounOf(summary)
  const month = format.monthLong(summary.period)

  return (
    <Region>
      <Answer>
        Your {widget.name} recorded <B>{format.qty(summary.used, noun)}</B> on {widget.site} in{' '}
        {month}.
      </Answer>

      <RegionCols>
        <Stack gap="tight">
          <HeroCap>
            {sentenceCase(pluralOf(summary.unit_noun))} recorded · {month}
          </HeroCap>
          <HeroFigure value={summary.used} />
          <Small>
            <Muted>
              {format.number(summary.days_elapsed)} of {format.number(summary.days_in_period)} days
            </Muted>
          </Small>
        </Stack>

        {/*
         * The second figure, and visibly subordinate to the first (P1). It is the
         * read model's `amount` — `billable × unit_amount`, computed by the API —
         * and never a multiplication written here.
         *
         * A widget not charged per unit has no cost so far to state — it is always
         * nothing — so the figure is its price instead, in the words every page
         * uses for it: "$5.00 a month", "Free" (session 46c).
         */}
        {isMetered(summary.unit_amount.amount) ? (
          <Stack gap="tight">
            <HeroCap>Cost so far</HeroCap>
            <SubFigure value={summary.amount} />
          </Stack>
        ) : (
          <Stack gap="tight">
            <HeroCap>Price</HeroCap>
            <SubFigure value={widgetPriceWords(widget, format)} />
          </Stack>
        )}

        <Kv bare>
          <KvRow k="Embedded at" v={widget.site} />
          {/*
           * `live_since` is a `DateOnly`, and `DayText` is the primitive for one:
           * `new Date('2026-04-12')` is midnight UTC, which in New York is the
           * 11th, so a day is read at noon UTC. This file, `WidgetUsagePanel` and
           * `InvoiceTable` each wrote that instant out by hand before `DayText`
           * and `useFormat().day` existed, which is what their comments say.
           */}
          <KvRow k="Live since" v={<DayText value={widget.live_since} />} />
          <KvRow k="Billing mode" v={BILLING_MODE_SENTENCE[summary.mode]} />
        </Kv>
      </RegionCols>
    </Region>
  )
}
